import React, { useState } from 'react';
import { X, MapPin, Mail, Phone, User, Building2 } from 'lucide-react';
import { getAPIRoot } from '../../services/api';

const VenueNominationModal = ({ isOpen, onClose, pageType = 'stag' }) => {
  const [formData, setFormData] = useState({
    venueName: '',
    email: '',
    phone: '',
    userType: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.venueName.trim()) {
      newErrors.venueName = 'Venue name is required';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[+]?[0-9\s\-()]{10,}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.userType) {
      newErrors.userType = 'Please select your user type';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${getAPIRoot()}/venue-nominations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          pageType,
          submittedAt: new Date().toISOString()
        }),
      });
      
      if (response.ok) {
        setSubmitSuccess(true);
        setTimeout(() => {
          setSubmitSuccess(false);
          setFormData({ venueName: '', email: '', phone: '', userType: '' });
          onClose();
        }, 2000);
      } else {
        throw new Error('Failed to submit nomination');
      }
    } catch (error) {
      console.error('Error submitting nomination:', error);
      setErrors({ submit: 'Failed to submit nomination. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;


  return (
    <div
      className="venue-nomination-modal fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="venue-nomination-modal__panel w-full max-w-md bg-gradient-to-br from-[#1a1a2e] to-[#0f0f23] border border-[#ff5f8f]/30 rounded-2xl shadow-[0_25px_60px_rgba(255,95,143,0.28)] overflow-hidden"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#ff5f8f]/20 bg-gradient-to-r from-[#ff5f8f]/12 to-transparent">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-[#ff5f8f] to-[#ec4899] rounded-full flex items-center justify-center shadow-[0_8px_25px_rgba(255,95,143,0.4)]">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-white text-xl font-bold">
              Nominate a Venue
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {submitSuccess ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-lg">✓</span>
                </div>
              </div>
              <h3 className="text-white text-lg font-semibold mb-2">Thank You!</h3>
              <p className="text-white/70 text-sm">
                Your nomination has been submitted successfully.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Venue Name */}
              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  Venue Name *
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
                  <input
                    type="text"
                    value={formData.venueName}
                    onChange={(e) => handleInputChange('venueName', e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 bg-white/5 border rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#ff0055]/50 focus:border-[#ff0055]/50 transition-all duration-200 ${
                      errors.venueName ? 'border-red-500' : 'border-[#ff0055]/30'
                    }`}
                    placeholder="Enter venue name"
                  />
                </div>
                {errors.venueName && (
                  <p className="text-red-400 text-xs mt-1">{errors.venueName}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  Your Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 bg-white/5 border rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#ff0055]/50 focus:border-[#ff0055]/50 transition-all duration-200 ${
                      errors.email ? 'border-red-500' : 'border-[#ff0055]/30'
                    }`}
                    placeholder="your@email.com (optional)"
                  />
                </div>
                {errors.email && (
                  <p className="text-red-400 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  Your Phone Number *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 bg-white/5 border rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#ff0055]/50 focus:border-[#ff0055]/50 transition-all duration-200 ${
                      errors.phone ? 'border-red-500' : 'border-[#ff0055]/30'
                    }`}
                    placeholder="+91 98765 43210"
                  />
                </div>
                {errors.phone && (
                  <p className="text-red-400 text-xs mt-1">{errors.phone}</p>
                )}
              </div>

              {/* User Type */}
              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  User Type *
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 bg-white/5 border border-glass-strong rounded-lg cursor-pointer hover:bg-white/10 transition-colors">
                    <input
                      type="radio"
                      name="userType"
                      value="owner"
                      checked={formData.userType === 'owner'}
                      onChange={(e) => handleInputChange('userType', e.target.value)}
                      className="w-4 h-4 text-purple-500 focus:ring-purple-500/50"
                    />
                    <User className="w-4 h-4 text-white/60" />
                    <span className="text-white text-sm">I'm the owner</span>
                  </label>
                  <label className="flex items-center gap-3 p-3 bg-white/5 border border-glass-strong rounded-lg cursor-pointer hover:bg-white/10 transition-colors">
                    <input
                      type="radio"
                      name="userType"
                      value="customer"
                      checked={formData.userType === 'customer'}
                      onChange={(e) => handleInputChange('userType', e.target.value)}
                      className="w-4 h-4 text-purple-500 focus:ring-purple-500/50"
                    />
                    <User className="w-4 h-4 text-white/60" />
                    <span className="text-white text-sm">I'm a customer</span>
                  </label>
                </div>
                {errors.userType && (
                  <p className="text-red-400 text-xs mt-1">{errors.userType}</p>
                )}
              </div>

              {/* Submit Error */}
              {errors.submit && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 text-sm">{errors.submit}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#ff0055] to-[#ec4899] hover:from-[#e6004a] hover:to-[#db2777] text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_8px_25px_rgba(255,0,85,0.4)] hover:shadow-[0_12px_35px_rgba(255,0,85,0.6)] hover:scale-105 border border-[#ff0055]/30"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Submitting...</span>
                  </div>
                ) : (
                  'Submit Nomination'
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default VenueNominationModal;
